import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const glossaryTerms = [
    {
        term: 'API Load Testing',
        definition: 'The practice of simulating real-world traffic against an API to evaluate its performance under various load conditions. API load testing reveals how an endpoint responds as the number of concurrent users increases, helping teams identify bottlenecks before production incidents occur. Unlike functional testing, load testing focuses on non-functional qualities: speed, throughput, and reliability under stress.',
        related: ['Virtual Users', 'Throughput', 'Stress Testing']
    },
    {
        term: 'Average Response Time',
        definition: 'The arithmetic mean of all response times recorded during a load test. While useful as a quick summary, average response time can be misleading because a few extremely slow outliers can drag the average up, or — more dangerously — a small number of very slow requests can be hidden by a large number of fast ones. For this reason, percentile metrics (p90, p95, p99) are preferred for understanding real user experience.',
        related: ['p90 Latency', 'p95 Latency', 'p99 Latency']
    },
    {
        term: 'Concurrent Users',
        definition: 'The number of users (or virtual users) that are actively sending requests to an API at exactly the same time. Concurrent users create resource contention on the server — competing for database connections, CPU time, and memory. Higher concurrency typically increases response times and can trigger failures when the system reaches its capacity limits.',
        related: ['Virtual Users (VUs)', 'Connections']
    },
    {
        term: 'Error Rate',
        definition: 'The percentage of requests that returned an error response (typically HTTP 4xx or 5xx status codes) during a load test. A 0% error rate means every request succeeded. Error rates typically stay at 0% during light loads but begin to rise as the system approaches its breaking point — making this metric a key indicator of when your API starts degrading.',
        related: ['Status Code Distribution', 'Success Rate']
    },
    {
        term: 'Fixed Load Profile',
        definition: 'A load testing pattern where a constant number of virtual users send requests throughout the entire test duration. Fixed load profiles are used for baseline performance measurement — establishing how your API performs under a known, steady level of traffic. This is the simplest load profile and is useful for regression testing between deployments.',
        related: ['Ramp-Up Testing', 'Spike Testing', 'Peak Load']
    },
    {
        term: 'Latency',
        definition: 'The time elapsed between sending a request and receiving the first byte of the response. In API testing, latency is often used interchangeably with response time, though technically latency refers to the network and processing delay, while response time includes the full data transfer. Lower latency means a faster, more responsive API.',
        related: ['Response Time', 'p99 Latency']
    },
    {
        term: 'Load Profile',
        definition: 'The pattern or shape of traffic applied during a load test. Different load profiles simulate different real-world scenarios. Common profiles include: Fixed (constant traffic), Ramp Up (gradually increasing), Spike (sudden burst), and Peak (sustained high traffic). Choosing the right load profile depends on what production scenario you\'re trying to validate.',
        related: ['Fixed Load Profile', 'Ramp-Up Testing', 'Spike Testing', 'Peak Load']
    },
    {
        term: 'p50 Latency (Median)',
        definition: 'The 50th percentile response time, meaning half (50%) of all requests completed faster than this value, and half took longer. P50 is the "typical" user experience. If your p50 is 80ms, then half of your users get a response in 80ms or less.',
        related: ['p90 Latency', 'p95 Latency', 'p99 Latency']
    },
    {
        term: 'p90 Latency',
        definition: 'The 90th percentile response time: 90% of all requests completed faster than this value. P90 is where you start seeing "slower-than-average" experiences. If your p90 is 200ms but your average is 80ms, it means 10% of your users are experiencing response times 2.5× worse than the average suggests.',
        related: ['p95 Latency', 'p99 Latency', 'Tail Latency']
    },
    {
        term: 'p95 Latency',
        definition: 'The 95th percentile response time: 95% of all requests completed faster than this value. P95 is commonly used in SLAs (Service Level Agreements) as the benchmark for acceptable performance. For example, a typical SLA might state "p95 response time must be under 300ms."',
        related: ['p90 Latency', 'p99 Latency', 'SLA']
    },
    {
        term: 'p99 Latency',
        definition: 'The 99th percentile response time: 99% of all requests completed faster than this value. P99 captures the worst-case experience for all but the most extreme outliers. It\'s critical because your heaviest or most valuable users often hit these tail latencies — and a 5-second p99 can destroy trust even if the average is 50ms. At scale, 1% of millions of requests is still thousands of poor experiences.',
        related: ['p90 Latency', 'p95 Latency', 'Tail Latency']
    },
    {
        term: 'Peak Load',
        definition: 'A load testing profile where a high number of virtual users is sustained for an extended period. Peak load testing simulates the busiest periods your API will face — like lunch hour traffic, end-of-month processing, or seasonal spikes. It differs from spike testing in that the high load is sustained, not momentary.',
        related: ['Spike Testing', 'Load Profile']
    },
    {
        term: 'Ramp-Up Testing',
        definition: 'A load testing technique where the number of virtual users gradually increases over the test duration, from a low starting point to a high maximum. Ramp-up testing is the most effective way to find your API\'s breaking point because you can observe exactly when response times start degrading and where errors begin appearing. For example, a test might ramp from 10 VUs to 200 VUs over 60 seconds.',
        related: ['Load Profile', 'Virtual Users (VUs)']
    },
    {
        term: 'Requests Per Second (RPS)',
        definition: 'A measure of throughput — the number of API requests completed in one second. RPS depends on both the number of concurrent users and the response time of each request. Higher RPS indicates the API can handle more traffic. A typical REST API might handle anywhere from 50 to 10,000+ RPS depending on complexity and infrastructure.',
        related: ['Throughput', 'Virtual Users (VUs)']
    },
    {
        term: 'Response Time',
        definition: 'The total time taken for an API to receive a request, process it, and return the complete response. Response time includes network latency, server processing time, database query time, and data transfer time. It\'s the most user-visible performance metric — when users say "this app is slow," they\'re experiencing high response times.',
        related: ['Latency', 'p99 Latency']
    },
    {
        term: 'Spike Testing',
        definition: 'A load testing technique that simulates a sudden, dramatic increase in traffic — mimicking scenarios like a product going viral on social media, a flash sale opening, or a DDoS-like traffic surge. Spike tests validate whether your auto-scaling infrastructure can react fast enough and whether your API degrades gracefully or crashes under sudden pressure.',
        related: ['Load Profile', 'Peak Load']
    },
    {
        term: 'Status Code Distribution',
        definition: 'A breakdown of all HTTP status codes returned during a load test. Healthy APIs under load should return primarily 200 (OK) responses. As load increases, you may see 429 (Rate Limited), 500 (Server Error), 502 (Bad Gateway), or 503 (Service Unavailable) codes appear — each pointing to different infrastructure bottlenecks.',
        related: ['Error Rate', 'Success Rate']
    },
    {
        term: 'Stress Testing',
        definition: 'A type of performance testing that pushes an API beyond its expected maximum load to find its absolute breaking point. While load testing validates performance under expected conditions, stress testing answers: "What happens when traffic exceeds our capacity?" It reveals failure modes — does the API crash, slow down gracefully, or start returning errors?',
        related: ['API Load Testing', 'Spike Testing']
    },
    {
        term: 'Success Rate',
        definition: 'The percentage of requests that returned a successful response (typically HTTP 2xx status codes) during a load test. A 100% success rate means every request received a successful response. The inverse of error rate. Industry best practice targets a success rate above 99% under expected production load.',
        related: ['Error Rate', 'Status Code Distribution']
    },
    {
        term: 'Tail Latency',
        definition: 'The response times experienced by the slowest requests — typically measured at p95, p99, or p99.9. Tail latency matters because at scale, even a small percentage of slow requests translates to a significant number of affected users. If you serve 1 million requests per day, a 1% tail (p99) means 10,000 users experience the worst latency.',
        related: ['p99 Latency', 'p95 Latency']
    },
    {
        term: 'Throughput',
        definition: 'The volume of data or requests an API can process per unit of time, typically measured in requests per second (RPS). Throughput is a capacity metric — it tells you how much work your system can handle. Throughput often increases as you add virtual users, but eventually plateaus or decreases as the system becomes saturated.',
        related: ['Requests Per Second (RPS)', 'Virtual Users (VUs)']
    },
    {
        term: 'Virtual Users (VUs)',
        definition: 'Simulated concurrent users in a load test. Each VU represents one user continuously sending requests to your API for the duration of the test. VUs create concurrent load — 50 VUs means 50 simultaneous request streams hitting your API at the same time. This simulates resource contention (database connections, CPU, memory) that doesn\'t appear in single-user testing.',
        related: ['Concurrent Users', 'Connections']
    },
];

const GlossaryPage = () => {
    const navigate = useNavigate();

    const definedTermSchema = {
        "@context": "https://schema.org",
        "@type": "DefinedTermSet",
        "name": "API Load Testing Glossary",
        "description": "Comprehensive glossary of API load testing and performance testing terminology",
        "url": "https://www.apiloadtest.com/glossary",
        "hasDefinedTerm": glossaryTerms.map(item => ({
            "@type": "DefinedTerm",
            "name": item.term,
            "description": item.definition,
            "url": `https://www.apiloadtest.com/glossary#${item.term.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
        }))
    };

    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.apiloadtest.com" },
            { "@type": "ListItem", "position": 2, "name": "Glossary", "item": "https://www.apiloadtest.com/glossary" }
        ]
    };

    return (
        <div style={styles.page}>
            <Helmet>
                <title>API Load Testing Glossary — Terms & Definitions | APILoadTest.com</title>
                <meta name="description" content="Glossary of API load testing terms: p99 latency, virtual users, ramp-up testing, throughput, spike testing, and more. Clear definitions for developers and QA engineers." />
                <meta name="keywords" content="p99 latency definition, virtual users load testing, ramp-up testing, throughput definition, API load testing terms, performance testing glossary" />
                <link rel="canonical" href="https://www.apiloadtest.com/glossary" />
                <meta property="og:title" content="API Load Testing Glossary | APILoadTest.com" />
                <meta property="og:description" content="Clear definitions of API load testing terms: p99 latency, virtual users, ramp-up testing, and more." />
                <meta property="og:url" content="https://www.apiloadtest.com/glossary" />
                <meta property="og:type" content="website" />
                <meta name="robots" content="index, follow" />
                <script type="application/ld+json">{JSON.stringify(definedTermSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
            </Helmet>

            {/* Header */}
            <header style={styles.header}>
                <div style={styles.headerInner}>
                    <Link to="/" style={styles.logoSection}>
                        <img src="/logo.png" alt="APILoadTest.com" style={styles.logoImage} />
                        <span style={styles.logoText}>APILoadTest.com</span>
                    </Link>
                    <nav style={styles.navLinks}>
                        <Link to="/#features" style={styles.navLink}>Features</Link>
                        <Link to="/#pricing" style={styles.navLink}>Pricing</Link>
                        <Link to="/blog" style={styles.navLink}>Blog</Link>
                        <Link to="/faq" style={styles.navLink}>FAQ</Link>
                    </nav>
                    <button style={styles.cta} onClick={() => navigate('/login')}>Get Started</button>
                </div>
            </header>

            {/* Breadcrumb */}
            <nav style={styles.breadcrumb} aria-label="Breadcrumb">
                <Link to="/" style={styles.breadcrumbLink}>Home</Link>
                <span style={styles.sep}>/</span>
                <span style={styles.breadcrumbCurrent}>Glossary</span>
            </nav>

            {/* Hero */}
            <section style={styles.hero}>
                <h1 style={styles.heroTitle}>API Load Testing Glossary</h1>
                <p style={styles.heroSub}>Clear, developer-friendly definitions for every key term in API performance and load testing.</p>
            </section>

            {/* Glossary */}
            <main style={styles.main}>
                <div style={styles.termsList}>
                    {glossaryTerms.map((item, idx) => (
                        <article
                            key={idx}
                            id={item.term.toLowerCase().replace(/[^a-z0-9]+/g, '-')}
                            style={styles.termCard}
                        >
                            <h2 style={styles.termName}>{item.term}</h2>
                            <p style={styles.termDef}>{item.definition}</p>
                            {item.related && item.related.length > 0 && (
                                <div style={styles.relatedRow}>
                                    <span style={styles.relatedLabel}>Related:</span>
                                    {item.related.map((rel, rIdx) => (
                                        <a
                                            key={rIdx}
                                            href={`#${rel.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                                            style={styles.relatedTag}
                                        >
                                            {rel}
                                        </a>
                                    ))}
                                </div>
                            )}
                        </article>
                    ))}
                </div>
            </main>

            {/* CTA */}
            <section style={styles.ctaSection}>
                <h2 style={styles.ctaTitle}>Put these concepts into practice</h2>
                <p style={styles.ctaSub}>Run your first API load test in seconds — no scripting, no setup.</p>
                <button style={styles.ctaButton} onClick={() => navigate('/login')}>Start Load Testing →</button>
            </section>

            {/* Footer */}
            <footer style={styles.footer}>
                <div style={styles.footerInner}>
                    <div style={styles.footerLinks}>
                        <a href="/terms" style={styles.footerLink}>Terms</a>
                        <a href="/privacy" style={styles.footerLink}>Privacy</a>
                        <a href="/refund" style={styles.footerLink}>Refund</a>
                        <a href="/faq" style={styles.footerLink}>FAQ</a>
                        <a href="/glossary" style={{ ...styles.footerLink, color: '#6366f1' }}>Glossary</a>
                        <a href="mailto:admin@apiloadtest.com" style={styles.footerLink}>Contact</a>
                    </div>
                    <p style={styles.copyright}>© 2026 APILoadTest.com · All rights reserved</p>
                </div>
            </footer>

            <style>{`
                @media (max-width: 768px) {
                    .glossary-nav-links { display: none !important; }
                }
            `}</style>
        </div>
    );
};

const styles = {
    page: { minHeight: '100vh', background: '#F7F8FC', fontFamily: "'DM Sans', 'Inter', sans-serif" },
    header: { background: '#fff', padding: '16px 0', position: 'sticky', top: 0, zIndex: 100, borderBottom: '1px solid #E4E5EF' },
    headerInner: { maxWidth: '1200px', margin: '0 auto', padding: '0 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    logoSection: { display: 'flex', alignItems: 'center', gap: '12px', textDecoration: 'none' },
    logoImage: { height: '40px', width: 'auto' },
    logoText: { fontFamily: "'Fraunces', serif", fontSize: '20px', fontWeight: '700', color: '#0A0D1C' },
    navLinks: { display: 'flex', gap: '32px', className: 'glossary-nav-links' },
    navLink: { color: '#3D3F52', textDecoration: 'none', fontSize: '15px', fontWeight: '500' },
    cta: { background: '#6366f1', color: '#fff', border: 'none', padding: '12px 28px', borderRadius: '24px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' },
    breadcrumb: { maxWidth: '900px', margin: '0 auto', padding: '24px', fontSize: '14px' },
    breadcrumbLink: { color: '#6366f1', textDecoration: 'none' },
    sep: { margin: '0 8px', color: '#8B8FA8' },
    breadcrumbCurrent: { color: '#8B8FA8' },
    hero: { textAlign: 'center', padding: '40px 24px 48px', maxWidth: '700px', margin: '0 auto' },
    heroTitle: { fontFamily: "'Fraunces', serif", fontSize: '48px', fontWeight: '900', color: '#0A0D1C', letterSpacing: '-2px', marginBottom: '16px' },
    heroSub: { fontSize: '18px', color: '#8B8FA8', lineHeight: '1.6' },
    main: { maxWidth: '900px', margin: '0 auto', padding: '0 24px 64px' },
    termsList: { display: 'flex', flexDirection: 'column', gap: '16px' },
    termCard: { background: '#fff', borderRadius: '14px', padding: '32px', boxShadow: '0 2px 12px rgba(10,13,28,.05)', border: '1px solid #E4E5EF' },
    termName: { fontFamily: "'Fraunces', serif", fontSize: '22px', fontWeight: '700', color: '#0A0D1C', marginBottom: '12px' },
    termDef: { fontSize: '16px', lineHeight: '1.8', color: '#3D3F52', margin: '0 0 16px 0' },
    relatedRow: { display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' },
    relatedLabel: { fontSize: '13px', fontWeight: '600', color: '#8B8FA8' },
    relatedTag: { fontSize: '12px', fontWeight: '600', color: '#6366f1', background: 'rgba(99,102,241,.08)', padding: '4px 12px', borderRadius: '100px', textDecoration: 'none', transition: 'background 0.2s' },
    ctaSection: { background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', padding: '80px 24px', textAlign: 'center' },
    ctaTitle: { fontSize: '36px', fontWeight: '700', color: '#fff', marginBottom: '16px', fontFamily: "'Fraunces', serif" },
    ctaSub: { fontSize: '18px', color: 'rgba(255,255,255,.9)', marginBottom: '32px' },
    ctaButton: { background: '#fff', color: '#6366f1', border: 'none', padding: '16px 40px', borderRadius: '30px', fontSize: '16px', fontWeight: '700', cursor: 'pointer' },
    footer: { background: '#fff', padding: '40px 24px', borderTop: '1px solid #E4E5EF' },
    footerInner: { maxWidth: '1200px', margin: '0 auto', textAlign: 'center' },
    footerLinks: { display: 'flex', gap: '24px', justifyContent: 'center', marginBottom: '16px' },
    footerLink: { color: '#8B8FA8', textDecoration: 'none', fontSize: '14px' },
    copyright: { fontSize: '14px', color: '#8B8FA8' },
};

export default GlossaryPage;
